import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { map, type Observable } from "rxjs";

import type { ApiPaginatedResponse, PaginatedResponse, Pagination } from "@/app/shared/models";
import { environment } from "@/environments/environment";
import type { GridCondition } from "./detail/condition-form/condition-form.models";
import type {
  ConfirmResponse,
  GridFull,
  GridType,
  MigrationStats,
  MigrationStatus,
  PreviewResponse,
  SupplierDetail,
  SupplierListItem,
  SupplierRaw,
} from "./shared/models";

/**
 * Provides HTTP operations for supplier management, shipping grid import, and migration tracking.
 */
@Injectable({
  providedIn: "root",
})
export class SupplierService {
  private httpClient = inject(HttpClient);

  private apiUrl = `${environment.apiUrl}/supplier`;

  /**
   * Retrieves a paginated list of suppliers.
   */
  getSuppliers(pagination: Pagination): Observable<PaginatedResponse<SupplierListItem>> {
    let params = new HttpParams()
      .set("page", pagination.currentPage)
      .set("limit", pagination.limit)
      .set("direction", pagination.direction);

    if (pagination.sort) {
      params = params.set("sort", pagination.sort);
    }

    if (pagination.globalFilter) {
      params = params.set("globalFilter", pagination.globalFilter);
    }

    return this.httpClient
      .get<ApiPaginatedResponse<"suppliers", SupplierRaw>>(this.apiUrl, { params })
      .pipe(
        map((response) => ({
          data: response.suppliers.map((s) => this.toListItem(s)),
          pagination: {
            ...response.pagination,
            direction: pagination.direction,
            sort: pagination.sort,
          },
        })),
      );
  }

  /**
   * Retrieves a single supplier with full details (grids, mappings).
   */
  getSupplier(id: string): Observable<SupplierDetail> {
    return this.httpClient.get<SupplierDetail>(`${this.apiUrl}/${id}`);
  }

  /**
   * Retrieves the full detail of a shipping grid with all rate tiers and surcharges.
   */
  getGrid(supplierId: string, gridId: string): Observable<GridFull> {
    return this.httpClient.get<GridFull>(`${this.apiUrl}/${supplierId}/grid/${gridId}`);
  }

  /**
   * Deletes a shipping grid.
   */
  deleteGrid(supplierId: string, gridId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.apiUrl}/${supplierId}/grid/${gridId}`);
  }

  /**
   * Retrieves migration statistics across all suppliers.
   */
  getMigrationStats(): Observable<MigrationStats> {
    return this.httpClient.get<MigrationStats>(`${this.apiUrl}/migration-stats`);
  }

  // --- Grid import ---

  /**
   * Uploads an XLSX file for preview (parse + validate).
   */
  previewGridImport(
    supplierId: string,
    file: File,
    gridType: GridType,
    gridName: string,
  ): Observable<PreviewResponse> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("gridType", gridType);
    formData.append("gridName", gridName);

    return this.httpClient.post<PreviewResponse>(
      `${this.apiUrl}/${supplierId}/grid/preview`,
      formData,
    );
  }

  /**
   * Confirms an import from a preview token.
   *
   * The optional `conditions` array (Story 2.6) is persisted atomically with
   * the grid in a single transaction. Pass `undefined` (or omit) for grids
   * without applicability conditions.
   */
  confirmGridImport(
    supplierId: string,
    previewToken: string,
    gridName: string,
    conditions?: GridCondition[],
  ): Observable<ConfirmResponse> {
    const body: Record<string, unknown> = { previewToken, gridName };
    if (conditions !== undefined) {
      // biome-ignore lint/complexity/useLiteralKeys: TS noPropertyAccessFromIndexSignature requires bracket access
      body["conditions"] = conditions;
    }
    return this.httpClient.post<ConfirmResponse>(`${this.apiUrl}/${supplierId}/grid/confirm`, body);
  }

  /**
   * Downloads the XLSX template for a grid type (authenticated blob download).
   */
  downloadGridTemplate(gridType: GridType): void {
    this.httpClient
      .get(`${this.apiUrl}/grid/templates/${gridType}`, { responseType: "blob" })
      .subscribe((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `template_${gridType}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      });
  }

  private toListItem(supplier: SupplierRaw): SupplierListItem {
    const mappings = supplier.mappings ?? [];
    const refs = mappings.map((m) => `PS-${m.prestashopId}`).join(", ");

    return {
      id: supplier.id,
      name: supplier.name,
      prestashopRefs: refs || "—",
      gridCount: supplier.gridCount ?? 0,
      mappingCount: supplier.mappingCount ?? 0,
      migrationStatus: this.resolveStatus(supplier),
      updatedAt: supplier.updatedAt ?? null,
    };
  }

  private resolveStatus(supplier: SupplierRaw): MigrationStatus {
    const gridCount = supplier.gridCount ?? 0;
    const mappingCount = supplier.mappingCount ?? 0;

    if (gridCount === 0) {
      return "pending";
    }

    if (mappingCount > 0 && gridCount < mappingCount) {
      return "in_progress";
    }

    return "migrated";
  }
}
